// Backend API ile FCM bildirim servisi
// Base URL tek kaynaktan: src/config/api.js
import API_BASE_URL_DEFAULT from "../config/api";
export const API_BASE_URL = API_BASE_URL_DEFAULT;

// Attach Firebase ID token when available so backend can verify admin
async function getAuthHeaders() {
  try {
    const { auth } = await import("../firebase");
    const user = auth.currentUser;
    if (!user) return { "Content-Type": "application/json" };
    const token = await user.getIdToken();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  } catch (e) {
    return { "Content-Type": "application/json" };
  }
}

export const sendNotificationToUser = async (
  token,
  title,
  body,
  category,
  priority
) => {
  try {
    console.log("Backend API FCM istegi gonderiliyor:", {
      token: token.substring(0, 20) + "...",
      title,
      body,
      category,
      priority,
    });

    const response = await fetch(`${API_BASE_URL}/send-notification`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        token,
        title,
        body,
        category,
        priority,
      }),
    });

    const result = await response.json();

    if (response.ok && result.success) {
      console.log("✅ FCM bildirimi başarıyla gönderildi:", result);
      return result;
    } else {
      console.error("❌ FCM bildirim hatası:", result);
      return { success: false, error: result.error || "Bilinmeyen hata" };
    }
  } catch (error) {
    console.error("❌ Backend API bağlantı hatası:", error);
    return {
      success: false,
      error: "Backend sunucusuna bağlanılamıyor. Server.js çalışıyor mu?",
    };
  }
};

// Backend API ile toplu FCM bildirim servisi
export const sendBulkNotification = async (
  tokens,
  title,
  body,
  category,
  priority
) => {
  try {
    console.log("Backend API toplu FCM istegi gonderiliyor:", {
      tokenCount: tokens.length,
      title,
      body,
      category,
      priority,
    });

    const response = await fetch(`${API_BASE_URL}/send-bulk-notification`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        tokens,
        title,
        body,
        category,
        priority,
      }),
    });

    const result = await response.json();

    if (response.ok && result.success) {
      console.log("✅ Toplu FCM bildirimi basariyla gonderildi:", result);
      return result;
    } else {
      console.error("❌ Toplu FCM bildirim hatasi:", result);
      return { success: false, error: result.error || "Bilinmeyen hata" };
    }
  } catch (error) {
    console.error("❌ Backend API baglanti hatasi:", error);
    return {
      success: false,
      error: "Backend sunucusuna baglanilamıyor. Server.js calisiyır mu?",
    };
  }
};

// Topic broadcast (system announcements)
export const sendTopicBroadcast = async (topic, title, body, data = {}) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/notifications/topic-broadcast`,
      {
        method: "POST",
        headers: await getAuthHeaders(),
        body: JSON.stringify({ topic, title, body, data }),
      }
    );
    const result = await response.json();
    if (response.ok && result.success) {
      return result;
    }
    return { success: false, error: result.error || "Bilinmeyen hata" };
  } catch (e) {
    return { success: false, error: "Backend sunucusuna bağlanılamıyor." };
  }
};

export const subscribeAllUsersToTopic = async (topic) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/notifications/subscribe-all`,
      {
        method: "POST",
        headers: await getAuthHeaders(),
        body: JSON.stringify({ topic }),
      }
    );
    const result = await response.json();
    if (response.ok && result.success) {
      return result;
    }
    return { success: false, error: result.error || "Bilinmeyen hata" };
  } catch (e) {
    return { success: false, error: "Backend sunucusuna bağlanılamıyor." };
  }
};

// Send to all users via tokens fetched from backend
export const sendNotificationToAllUsers = async (
  title,
  body,
  category = "system",
  priority = "normal",
  segment = null
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/send-to-all`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify({ title, body, category, priority, segment }),
    });
    const result = await response.json();
    if (response.ok && result.success) {
      return result;
    }
    return { success: false, error: result.error || "Bilinmeyen hata" };
  } catch (e) {
    return { success: false, error: "Backend sunucusuna bağlanılamıyor." };
  }
};

// Fetch announcement logs from backend (avoids client Firestore listener issues)
export const fetchAnnouncementLogs = async (limit = 200, opts = {}) => {
  try {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (opts.start) qs.set("start", opts.start);
    if (opts.end) qs.set("end", opts.end);
    if (opts.type) qs.set("type", opts.type);
    const response = await fetch(
      `${API_BASE_URL}/announcement-logs?${qs.toString()}`
    );
    const result = await response.json();
    if (response.ok && result.success) return result.logs || [];
    return [];
  } catch (e) {
    return [];
  }
};

export const deleteAnnouncementLogs = async (ids = []) => {
  try {
    const response = await fetch(`${API_BASE_URL}/announcement-logs`, {
      method: "DELETE",
      headers: await getAuthHeaders(),
      body: JSON.stringify({ ids }),
    });
    const result = await response.json();
    if (response.ok && result.success) return result;
    return { success: false, error: result.error || "Silme işlemi başarısız" };
  } catch (e) {
    return { success: false, error: "Backend sunucusuna bağlanılamıyor." };
  }
};

// Announcements (draft/scheduled)
export const createAnnouncement = async (payload) => {
  try {
    const res = await fetch(`${API_BASE_URL}/announcements`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (res.ok && json.success) return json.announcement;
    throw new Error(json.error || "Oluşturma başarısız");
  } catch (e) {
    throw e;
  }
};

export const listAnnouncements = async ({ status, limit = 100 } = {}) => {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (status) qs.set("status", status);
  const res = await fetch(`${API_BASE_URL}/announcements?${qs.toString()}`);
  const json = await res.json();
  if (res.ok && json.success) return json.announcements || [];
  throw new Error(json.error || "Listeleme başarısız");
};

export const updateAnnouncement = async (id, payload) => {
  const res = await fetch(
    `${API_BASE_URL}/announcements/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  const json = await res.json();
  if (res.ok && json.success) return true;
  throw new Error(json.error || "Güncelleme başarısız");
};

export const deleteAnnouncement = async (id) => {
  const res = await fetch(
    `${API_BASE_URL}/announcements/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      headers: await getAuthHeaders(),
    }
  );
  const json = await res.json();
  if (res.ok && json.success) return true;
  throw new Error(json.error || "Silme başarısız");
};

export const sendAnnouncementNow = async (id) => {
  const res = await fetch(
    `${API_BASE_URL}/announcements/${encodeURIComponent(id)}/send`,
    {
      method: "POST",
      headers: await getAuthHeaders(),
    }
  );
  const json = await res.json();
  if (res.ok && json.success) return true;
  throw new Error(json.error || "Gönderim başarısız");
};

// Templates API
export const listTemplates = async (limit = 200) => {
  const res = await fetch(
    `${API_BASE_URL}/templates?limit=${encodeURIComponent(limit)}`
  );
  const json = await res.json();
  if (res.ok && json.success) return json.templates || [];
  throw new Error(json.error || "Şablonlar yüklenemedi");
};

export const createTemplate = async ({ key, locales }) => {
  const res = await fetch(`${API_BASE_URL}/templates`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify({ key, locales }),
  });
  const json = await res.json();
  if (res.ok && json.success) return json.template;
  throw new Error(json.error || "Şablon oluşturulamadı");
};

export const updateTemplate = async (id, payload) => {
  const res = await fetch(
    `${API_BASE_URL}/templates/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  const json = await res.json();
  if (res.ok && json.success) return true;
  throw new Error(json.error || "Şablon güncellenemedi");
};

export const deleteTemplate = async (id) => {
  const res = await fetch(
    `${API_BASE_URL}/templates/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      headers: await getAuthHeaders(),
    }
  );
  const json = await res.json();
  if (res.ok && json.success) return true;
  throw new Error(json.error || "Şablon silinemedi");
};
